import React, { useEffect, useState, useCallback } from 'react';
import { apiCall, useAuth } from '../AuthContext';
import {
  Lightbulb, Plus, ChevronUp, Loader2, X, Filter, MessageSquare, Trash2, Tag, Sparkles, CheckCircle2,
} from 'lucide-react';

const STATUS_STYLES = {
  'Under Review': 'bg-slate-100 text-slate-700',
  'Planned'     : 'bg-blue-100 text-blue-700',
  'In Progress' : 'bg-amber-100 text-amber-800',
  'Done'        : 'bg-emerald-100 text-emerald-700',
  'Declined'    : 'bg-red-50 text-red-700',
};

export default function FeatureSuggestions() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sort, setSort] = useState('top');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ title: '', description: '', category: 'Other' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const [adminEditing, setAdminEditing] = useState(null);
  const [adminDraft, setAdminDraft] = useState({ status: '', admin_reply: '' });
  const [adminBusy, setAdminBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort });
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      const r = await apiCall(`/api/feature-suggestions?${params}`, { method: 'GET' });
      setItems(r.data.items || []);
      setStatuses(r.data.statuses || []);
      setCategories(r.data.categories || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load suggestions');
    } finally { setLoading(false); }
  }, [sort, statusFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (draft.title.trim().length < 4) { setErr('Title must be at least 4 characters.'); return; }
    if (draft.description.trim().length < 10) { setErr('Description must be at least 10 characters.'); return; }
    setSubmitting(true); setErr('');
    try {
      await apiCall('/api/feature-suggestions', { method: 'POST', data: draft });
      setShowForm(false);
      setDraft({ title: '', description: '', category: 'Other' });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const toggleVote = async (id) => {
    // optimistic toggle
    setItems(prev => prev.map(i => i.id === id
      ? { ...i, my_voted: !i.my_voted, upvote_count: i.upvote_count + (i.my_voted ? -1 : 1) }
      : i));
    try {
      await apiCall(`/api/feature-suggestions/${id}/vote`, { method: 'POST' });
    } catch {
      // revert on failure
      load();
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this suggestion?')) return;
    try {
      await apiCall(`/api/feature-suggestions/${id}`, { method: 'DELETE' });
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const startAdminEdit = (s) => {
    setAdminEditing(s.id);
    setAdminDraft({ status: s.status, admin_reply: s.admin_reply || '' });
  };

  const saveAdminEdit = async () => {
    if (!adminEditing) return;
    setAdminBusy(true);
    try {
      await apiCall(`/api/admin/feature-suggestions/${adminEditing}`, {
        method: 'PUT', data: adminDraft,
      });
      setAdminEditing(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally { setAdminBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="feature-suggestions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A] inline-flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-amber-500" /> Feature Suggestions
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Got an idea? Share it. Upvote what you like. We build what the community wants most.
          </p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#0052FF] hover:bg-[#0040CC] text-white text-sm font-semibold transition-colors shadow-sm"
          data-testid="new-suggestion-btn">
          <Plus className="w-4 h-4" /> Submit a Feature
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 flex flex-wrap items-center gap-2 text-sm">
        <Filter className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" role="tablist">
          {['top', 'new'].map(s => (
            <button key={s} onClick={() => setSort(s)} data-testid={`sort-${s}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${sort === s ? 'bg-[#0052FF] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
              {s === 'top' ? 'Top' : 'Newest'}
            </button>
          ))}
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
          data-testid="filter-status">
          <option value="">All statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
          data-testid="filter-category">
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="ml-auto text-xs text-slate-400">{items.length} suggestion{items.length !== 1 ? 's' : ''}</span>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#0052FF] mx-auto" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-[#0F172A]">Be the first to suggest a feature!</h3>
          <p className="text-sm text-slate-500 mt-1">Click "Submit a Feature" above to share your idea.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(s => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`suggestion-${s.id}`}>
              <div className="flex">
                {/* Vote column */}
                <button onClick={() => toggleVote(s.id)} data-testid={`vote-${s.id}`}
                  className={`flex flex-col items-center justify-center px-4 py-5 border-r border-slate-100 transition-colors ${s.my_voted ? 'bg-[#0052FF]/5 hover:bg-[#0052FF]/10' : 'hover:bg-slate-50'}`}>
                  <ChevronUp className={`w-5 h-5 ${s.my_voted ? 'text-[#0052FF]' : 'text-slate-400'}`} strokeWidth={3} />
                  <span className={`text-base font-bold mt-0.5 ${s.my_voted ? 'text-[#0052FF]' : 'text-slate-700'}`} data-testid={`vote-count-${s.id}`}>
                    {s.upvote_count}
                  </span>
                </button>
                {/* Content */}
                <div className="flex-1 p-4 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold text-[#0F172A] break-words">{s.title}</h3>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${STATUS_STYLES[s.status] || 'bg-slate-100 text-slate-700'}`}
                      data-testid={`status-${s.id}`}>
                      {s.status.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap break-words">{s.description}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px] text-slate-400">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-slate-600 font-medium">
                      <Tag className="w-3 h-3" /> {s.category}
                    </span>
                    <span>by <span className="font-mono text-slate-500">{s.is_mine ? 'you' : s.submitted_by_username}</span></span>
                    <span>· {new Date(s.created_at).toLocaleDateString()}</span>
                    {(s.is_mine || isAdmin) && (
                      <button onClick={() => deleteItem(s.id)}
                        className="ml-auto inline-flex items-center gap-1 text-red-500 hover:text-red-700"
                        data-testid={`delete-${s.id}`}>
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}
                  </div>

                  {/* Admin reply */}
                  {s.admin_reply && (
                    <div className="mt-3 px-3 py-2.5 rounded-lg bg-blue-50/60 border-l-4 border-[#0052FF] text-sm">
                      <div className="text-[10px] uppercase tracking-wider text-[#0052FF] font-bold mb-1 inline-flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" /> Reply from team
                      </div>
                      <div className="text-slate-700 whitespace-pre-wrap">{s.admin_reply}</div>
                    </div>
                  )}

                  {/* Admin controls */}
                  {isAdmin && (
                    adminEditing === s.id ? (
                      <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50/50 space-y-2">
                        <select value={adminDraft.status} onChange={e => setAdminDraft(p => ({ ...p, status: e.target.value }))}
                          className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm w-full"
                          data-testid={`admin-status-select-${s.id}`}>
                          {statuses.map(st => <option key={st} value={st}>{st}</option>)}
                        </select>
                        <textarea value={adminDraft.admin_reply} onChange={e => setAdminDraft(p => ({ ...p, admin_reply: e.target.value }))}
                          rows={3} placeholder="Write a public reply to the customer (optional)…"
                          className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
                          data-testid={`admin-reply-textarea-${s.id}`} />
                        <div className="flex items-center gap-2">
                          <button onClick={saveAdminEdit} disabled={adminBusy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0052FF] text-white text-xs font-semibold hover:bg-[#0040CC] disabled:opacity-50"
                            data-testid={`admin-save-${s.id}`}>
                            {adminBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Save
                          </button>
                          <button onClick={() => setAdminEditing(null)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-white">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => startAdminEdit(s)}
                        className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 hover:text-amber-900"
                        data-testid={`admin-edit-${s.id}`}>
                        <MessageSquare className="w-3 h-3" /> Update status / reply
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !submitting && setShowForm(false)}
          data-testid="suggestion-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#0F172A] inline-flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" /> Submit a Feature
              </h3>
              <button onClick={() => !submitting && setShowForm(false)} className="text-slate-400 hover:text-slate-600 p-1"
                data-testid="suggestion-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Title</label>
                <input value={draft.title} onChange={e => setDraft(p => ({ ...p, title: e.target.value }))}
                  maxLength={140}
                  placeholder="One-line summary (e.g. 'Bulk-print labels for past batches')"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
                  data-testid="suggestion-title-input" />
                <div className="text-[10px] text-slate-400 mt-0.5 text-right">{draft.title.length}/140</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Category</label>
                <select value={draft.category} onChange={e => setDraft(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
                  data-testid="suggestion-category-select">
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Description</label>
                <textarea value={draft.description} onChange={e => setDraft(p => ({ ...p, description: e.target.value }))}
                  maxLength={2000} rows={5}
                  placeholder="Describe the feature, why it would help your workflow, and any examples or links."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
                  data-testid="suggestion-description-textarea" />
                <div className="text-[10px] text-slate-400 mt-0.5 text-right">{draft.description.length}/2000</div>
              </div>
              {err && (
                <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="suggestion-error">
                  {err}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} disabled={submitting}
                className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={submit} disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0052FF] text-white text-sm font-semibold hover:bg-[#0040CC] disabled:opacity-50"
                data-testid="suggestion-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
